import { useCallback, useEffect, useState } from 'react';
import { useShadowRoot } from '@backstage/plugin-techdocs-react';
import type { ReaderNav, ReaderNavEntry } from './types';

/*
 * The reader's doc-nav tree — the doc set's pages — read out of the shadow root.
 *
 * mkdocs builds the tree into every page and `readerStyles` hides it on the
 * phone breakpoint, where there is no room for a 281px rail. The nodes are still
 * there, so the phone's Contents sheet reads them rather than re-deriving the
 * doc set's structure from the catalog: this is the same tree the desktop rail
 * shows, and its anchors are the reader's own navigation.
 *
 * Rows carry their position in the nav's link list rather than a captured node.
 * The reader replaces its shadow content on every page, so a node held from an
 * earlier page is detached and its click goes nowhere; `select` re-queries.
 */
const NAV_LINK = '.md-sidebar--primary .md-nav--primary .md-nav__link';
const NAV_TITLE = '.md-sidebar--primary .md-nav--primary > .md-nav__title';

/** A real page link — a page of the doc set, not an in-page `#` heading. */
const PAGE_LINK = '.md-nav__link[href]:not([href*="#"])';

/*
 * Indentation counts only the real section groupings (`--nested` items) above a
 * row, not mkdocs' integrated-ToC wrapper (see `keptRows`), so the active page
 * sits level with its siblings rather than one notch in.
 */
const depthOf = (link: HTMLElement): number => {
  let depth = 1;
  const own = link.closest('.md-nav__item');
  for (
    let node = own?.parentElement;
    node && !node.classList.contains('md-nav--primary');
    node = node.parentElement
  ) {
    if (node.matches('li.md-nav__item--nested')) depth += 1;
  }
  return depth;
};

/*
 * At 402px mkdocs is in its own phone layout, so the primary nav integrates the
 * current page's table of contents into the tree: the active page is wrapped in
 * a null-href item whose nested nav holds the page's real link plus its headings
 * as `#` anchors. Those headings belong to "On this page", and the wrapper only
 * duplicates the page. A link earns a Contents row only when it is one of:
 *   - a real page (an href with no `#`);
 *   - a section that groups real pages (`--nested` with a page in its subtree);
 *   - the current page when mkdocs gives it no self-link at all.
 * The heading anchors, the integrated-ToC wrapper, and stray title links go.
 */
const keptRows = (root: ShadowRoot): { link: HTMLElement; index: number }[] =>
  Array.from(root.querySelectorAll<HTMLElement>(NAV_LINK))
    .map((link, index) => ({ link, index }))
    .filter(({ link }) => {
      const item = link.parentElement;
      if (!item?.classList.contains('md-nav__item')) return false;
      const href = link.getAttribute('href');
      if (href?.includes('#')) return false;
      if (href) return true;
      const nested = item.classList.contains('md-nav__item--nested');
      const hasPage = Boolean(item.querySelector(PAGE_LINK));
      if (nested) return hasPage;
      return item.classList.contains('md-nav__item--active') && !hasPage;
    });

const readEntries = (root: ShadowRoot): ReaderNavEntry[] =>
  keptRows(root).map(({ link, index }) => {
    const item = link.parentElement;
    const isSection = Boolean(
      item?.classList.contains('md-nav__item--nested'),
    );
    return {
      index,
      label: (link.textContent ?? '').trim(),
      depth: depthOf(link),
      /*
       * mkdocs marks every ancestor section active alongside the current page;
       * only the page itself takes the active row, matching the desktop rail's
       * own `:not(.md-nav__item--nested)` rule.
       */
      active:
        link.classList.contains('md-nav__link--active') ||
        (Boolean(item?.classList.contains('md-nav__item--active')) &&
          !isSection),
      isSection,
    };
  });

const readTitle = (root: ShadowRoot): string | undefined => {
  const title = root.querySelector<HTMLElement>(NAV_TITLE)?.textContent?.trim();
  return title || undefined;
};

const same = (a: ReaderNavEntry[], b: ReaderNavEntry[]) =>
  a.length === b.length &&
  a.every(
    (entry, i) =>
      entry.label === b[i].label &&
      entry.depth === b[i].depth &&
      entry.active === b[i].active &&
      entry.isSection === b[i].isSection,
  );

export const useReaderNav = (): ReaderNav => {
  const shadowRoot = useShadowRoot();
  const [entries, setEntries] = useState<ReaderNavEntry[]>([]);
  const [title, setTitle] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!shadowRoot) return undefined;
    /*
     * The observer fires for every mutation in the reader, including the ones the
     * other addons make, so the tree is only swapped when a row actually changed
     * — otherwise every mutation would re-render the sheet.
     */
    const sync = () => {
      const next = readEntries(shadowRoot);
      setEntries(current => (same(current, next) ? current : next));
      setTitle(readTitle(shadowRoot));
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(shadowRoot, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [shadowRoot]);

  const select = useCallback(
    (index: number) => {
      shadowRoot?.querySelectorAll<HTMLElement>(NAV_LINK).item(index)?.click();
    },
    [shadowRoot],
  );

  return {
    entries,
    title,
    pageCount: entries.filter(entry => !entry.isSection).length,
    select,
  };
};
